import { AfterContentInit, Component, ContentChildren, EventEmitter, HostBinding, Input, Optional, Output, QueryList } from '@angular/core';
import Enumerable from 'linq';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ReviewFormLineItemCondition } from '../../models/review-form/review-form-line-item-condition.model';
import { ReviewFormComponent } from '../review-form/review-form.component';
import { ReviewFormLineItemComponent } from '../review-form-line-item/review-form-line-item.component';

@Component({
  selector: 'app-review-form-section',
  host: {
    'class': 'tab-pane fade',
    'attr.role': 'tabpanel'
  },
  templateUrl: './review-form-section.component.html',
  styleUrl: './review-form-section.component.css'
})
export class ReviewFormSectionComponent implements AfterContentInit {
  @Output() onChange = new EventEmitter<number>();

  @HostBinding('attr.id') id = '';

  @HostBinding('attr.aria-labelledby') tabId = '';

  @HostBinding('class.active') active = false;

  @HostBinding('class.show') show = false;

  @Input({ required: true }) label!: string;

  @Input({ required: true }) readOnly!: boolean;

  @ContentChildren(ReviewFormLineItemComponent) lineItems!: QueryList<ReviewFormLineItemComponent>;

  public disabled = true;

  public lastSection = false;

  public firstSection = false;

  public formGroup = new FormGroup({});

  private index = -1;

  constructor(@Optional() public parent: ReviewFormComponent) {
  }

  ngAfterContentInit() {
    // Set up the line items and secion form group
    Enumerable.from(this.lineItems.toArray())
      .forEach((x, i) => {
        x.setIndex(i);

        const validators = this.getValidators(x);
        var formControl = new FormControl(x.value, validators);
        formControl.valueChanges.subscribe(y => this.onLineItemChange(x, y));
        if (this.readOnly) {
          formControl.disable();
        }

        this.formGroup.addControl(x.id, formControl);
        x.setFormGroup(this.formGroup);
      });

    // Process the line item conditions
    Enumerable.from(this.lineItems.toArray())
      .forEach(x => {
        this.onLineItemChange(x, x.value);
      });
  }

  public setReadOnly = (value: boolean) => {
    this.readOnly = value;
    if (this.readOnly) {
      Enumerable.from(this.formGroup.controls)
        .forEach(x => {
          var control = this.formGroup.get(x.key);
          control?.disable();
        });
    }
  }

  public onLineItemChange = (lineItem: ReviewFormLineItemComponent, newValue: string | null) => {
    lineItem.value = newValue;
    lineItem.flagged = Enumerable.from(lineItem.flags)
      .where(x => x.value == lineItem.value)
      .any();
    var childIds = Enumerable.from(lineItem.conditions)
      .select(x => x.childId)
      .distinct()
      .toArray();
    if (childIds.length == 0) {
      return;
    }

    const lineItems = this.lineItems.toArray();
    Enumerable.from(lineItems)
      .selectMany(x => x.conditions, (x, y) => ({ lineItem: x, condition: <ReviewFormLineItemCondition>y }))
      .join(Enumerable.from(childIds), x => x.condition.childId, y => y, x => x)
      .groupBy(x => x.condition.childId)
      .select(x => ({ childId: x.key(), lineItems: x.select(y => y.lineItem).toArray(), condition: x.select(y => y.condition).first() }))
      .join(Enumerable.from(lineItems), x => x.childId, y => y.questionId, (x, y) => ({ childLineItem: y, lineItems: x.lineItems, condition: x.condition }))
      .forEach(x => {
        const showChild = Enumerable.from(x.lineItems)
          .select(y => y.value == x.condition.value)
          .where(y => y)
          .any();

        x.childLineItem.setDisplay(showChild);

        const childFormItem = this.formGroup.get(x.childLineItem.id);
        childFormItem?.clearValidators();
        if (showChild) {
          const validators = this.getValidators(x.childLineItem);
          childFormItem?.setValidators(validators);
        }

        childFormItem?.updateValueAndValidity();
      });
  }

  public setShow = (value: boolean) => {
    this.show = value;
    return this;
  }

  public setActive = (value: boolean) => {
    this.active = value;
    return this;
  }

  public setDisabled = (value: boolean) => {
    this.disabled = value;
    return this;
  }

  public setLastSection = (value: boolean) => {
    this.lastSection = value;
    return this;
  }

  public setIndex = (index: number) => {
    this.index = index;
    this.id = `section-${index.toString().padStart(2, '0')}`;
    this.tabId = `${this.id}-tab`;
    this.active = this.show = this.firstSection = index == 0;
    this.disabled = this.isValid()
      ? false
      : index != 0;
    return this;
  }

  public getIndex = () => this.index;

  public onNextClick = () => this.parent.onNextClick(this);

  public onPreviousClick = () => this.parent.onPreviousClick(this);

  public onSaveClick = () => this.parent.onSaveClick(this);

  public isValid = () => (this.formGroup?.valid ?? false) || this.readOnly;

  public parentIsValid = () => this.parent.isValid();

  public isFlagged = () => Enumerable.from(this.lineItems.toArray())
    .where(x => x.flagged)
    .any();

  public hasChanges = () => this.formGroup.dirty;

  private getValidators = (lineItem: ReviewFormLineItemComponent) => {
    var validators = [];
    if (lineItem.required) {
      validators.push(Validators.required)
    }

    return validators;
  }
}
